import React, { useEffect, useRef, useState } from 'react';
import TeamName from './TeamName';

// Broadcast matchup card — the one component behind the match queue, the Now
// Playing court panels, match history rows and the owner console tables
// (checklist 3.1). Symmetric stacked pairs, neutral names, team accent bars,
// VS diamond medallion; the medallion slot switches state:
//   queued    → VS diamond + "Court in ~X min"
//   live      → big tabular score (counts up, never reflows)
//   completed → game-score pills; winner tab + loser dim (NO strikethrough)
//   compact   → denser queued layout for owner tables

export const TEAM_A_COLOR = '#E63946';
export const TEAM_B_COLOR = '#3B82F6';
const GOLD = '#F59E0B';
const GREEN = '#22C55E';

// One formatter for every wait display — bench rows import this too.
// null → null · ≤0 → 'up now' handled by caller · >90 → honest cap.
export function fmtWait(minutes) {
  if (minutes == null) return null;
  if (minutes > 90) return '90+ min';
  return `~${Math.round(minutes)} min`;
}

function useCountUp(value) {
  const [shown, setShown] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current;
    const to = Number(value) || 0;
    if (from === to) return undefined;
    prev.current = to;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / 400);
      setShown(Math.round(from + (to - from) * k));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return shown;
}

function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/);
  return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase() || '?';
}

function Avatar({ player, color, compact }) {
  // Responsive: 18px on phones, 22px from sm: — every horizontal pixel goes to names
  const sizeCls = compact
    ? 'h-[17px] w-[17px] text-[7px]'
    : 'h-[18px] w-[18px] text-[7.5px] sm:h-[22px] sm:w-[22px] sm:text-[9px]';
  if (player.avatar_url) {
    return (
      <img src={player.avatar_url} alt={player.name}
        className={`rounded-full object-cover shrink-0 ${sizeCls}`}
        style={{ border: `1px solid ${color}55` }} />
    );
  }
  return (
    <span className={`rounded-full shrink-0 inline-flex items-center justify-center font-black ${sizeCls}`}
      style={{ background: `${color}20`, color, border: `1px solid ${color}40`,
        fontFamily: 'Oswald, sans-serif' }}>
      {initialsOf(player.name)}
    </span>
  );
}

function TeamStack({ side, players, names, color, dim, winnerTab, highlight, compact, stacked = false }) {
  // stacked (phone) mode: both teams read as a list — bar left, text left
  const isA = stacked ? false : side === 'a';
  const barSide = stacked ? 'left' : (isA ? 'left' : 'right');
  const rows = (players && players.length > 0)
    ? players
    : (names || []).map((n, i) => ({ id: `n${i}`, name: n }));
  return (
    <div className="flex flex-col min-w-0 relative"
      style={{
        gap: compact ? 3 : 6,
        alignItems: isA ? 'flex-end' : 'flex-start',
        padding: compact ? '1px 9px' : '2px clamp(8px, 2.5vw, 12px)',
      }}>
      <span aria-hidden="true" style={{
        position: 'absolute', top: 3, bottom: 3, width: 3, borderRadius: 3,
        [barSide]: 0,
        background: color,
        boxShadow: highlight ? `0 0 10px ${color}66` : 'none',
        opacity: dim ? 0.4 : 1,
      }} />
      {winnerTab && (
        <span className="uppercase font-black rounded"
          style={{ fontSize: 9, letterSpacing: '0.1em', color: GOLD, padding: '1px 5px',
            border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.1)',
            fontFamily: 'Oswald, sans-serif' }}>
          WINNER
        </span>
      )}
      {rows.length === 0 && <TeamName name="TBD" dim size={compact ? 12 : null} />}
      {rows.map((p) => (
        <span key={p.id || p.name} className="flex items-center min-w-0 max-w-full"
          style={{ gap: 'clamp(5px, 1.5vw, 7px)', flexDirection: isA ? 'row-reverse' : 'row', opacity: dim ? 0.55 : 1 }}>
          <Avatar player={p} color={color} compact={compact} />
          <TeamName name={p.name} bold={highlight} size={compact ? 12 : null} />
        </span>
      ))}
    </div>
  );
}

function VsDiamond({ compact }) {
  const s = compact ? 20 : 30;
  return (
    <span className="flex items-center justify-center shrink-0"
      style={{ width: s, height: s, transform: 'rotate(45deg)', background: '#0D0D0D',
        border: '1px solid #333', borderRadius: compact ? 5 : 7 }}>
      <span style={{ transform: 'rotate(-45deg)', fontFamily: 'Oswald, sans-serif', fontWeight: 900,
        fontSize: compact ? 8 : 10, color: '#525252', letterSpacing: '0.05em' }}>
        VS
      </span>
    </span>
  );
}

function EtaChip({ minutes }) {
  if (minutes == null) return null;
  const free = minutes <= 0;
  const label = free ? 'Court is free — up now' : `Court in ${fmtWait(minutes)}`;
  const color = free ? GREEN : GOLD;
  return (
    <span className="font-bold rounded-full whitespace-nowrap"
      style={{ fontSize: 10.5, color, padding: '2px 8px',
        background: `${color}14`, border: `1px solid ${color}30` }}>
      {label}
    </span>
  );
}

function LiveScore({ scoreA, scoreB, note }) {
  const a = useCountUp(Number(scoreA) || 0);
  const b = useCountUp(Number(scoreB) || 0);
  return (
    <>
      <span className="flex items-baseline rounded-xl"
        style={{ gap: 7, background: '#000', border: '1px solid #333', padding: '4px 12px',
          fontFamily: 'Oswald, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ fontSize: 24, fontWeight: 900, color: TEAM_A_COLOR }}>{a}</span>
        <span style={{ fontSize: 16, color: '#3A3A3A' }}>—</span>
        <span style={{ fontSize: 24, fontWeight: 900, color: TEAM_B_COLOR }}>{b}</span>
      </span>
      {note && <span style={{ fontSize: 10, color: '#525252' }}>{note}</span>}
    </>
  );
}

function GamePills({ games, winner }) {
  return (
    <span className="flex" style={{ gap: 4 }}>
      {(games || []).map((g, i) => (
        <span key={i} className="rounded-md font-extrabold"
          style={{ fontSize: 11, padding: '2px 7px', background: '#0D0D0D',
            border: '1px solid #2E2E2E', color: '#A3A3A3',
            fontFamily: 'Oswald, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ color: winner === 'b' ? '#A3A3A3' : '#FFFFFF' }}>{g.a}</span>
          –
          <span style={{ color: winner === 'b' ? '#FFFFFF' : '#A3A3A3' }}>{g.b}</span>
        </span>
      ))}
    </span>
  );
}

export default function MatchupCard({
  variant = 'queued',
  teamA = [], teamB = [], namesA = [], namesB = [],
  etaMinutes = null,
  nextUp = false,
  forceStacked = false,
  scoreA = 0, scoreB = 0, scoreNote = null,
  winner = null,
  games = null,
  highlight = null,
  style = {},
  'data-testid': testId,
}) {
  const compact = variant === 'compact';
  const completed = variant === 'completed';
  const live = variant === 'live';

  const teamProps = (side) => ({
    players: side === 'a' ? teamA : teamB,
    names: side === 'a' ? namesA : namesB,
    color: side === 'a' ? TEAM_A_COLOR : TEAM_B_COLOR,
    dim: completed && winner === (side === 'a' ? 'b' : 'a'),
    winnerTab: completed && winner === side,
    highlight: highlight === side,
    compact,
  });
  const center = live ? (
    <LiveScore scoreA={scoreA} scoreB={scoreB} note={scoreNote} />
  ) : completed ? (
    <GamePills games={games || [{ a: scoreA, b: scoreB }]} winner={winner} />
  ) : (
    <>
      <VsDiamond compact={compact} />
      {!compact && <EtaChip minutes={etaMinutes} />}
    </>
  );
  const nextUpTab = nextUp && (
    <span className="absolute font-black uppercase"
      style={{ top: -1, left: '50%', transform: 'translateX(-50%)', fontSize: 8.5,
        letterSpacing: '0.14em', color: '#0A0A0A', background: GOLD,
        padding: '1.5px 9px', borderRadius: '0 0 6px 6px', fontFamily: 'Oswald, sans-serif' }}>
      NEXT UP
    </span>
  );

  // Compact is always the symmetric grid; full cards switch to a stacked layout
  // below sm: — side-by-side columns can't fit two full names on a phone.
  if (compact) {
    return (
      <div className="relative grid items-center" data-testid={testId}
        style={{ gridTemplateColumns: 'minmax(0,1fr) 52px minmax(0,1fr)', padding: '7px 10px', ...style }}>
        <TeamStack side="a" {...teamProps('a')} />
        <div className="flex flex-col items-center" style={{ gap: 5 }}>{center}</div>
        <TeamStack side="b" {...teamProps('b')} />
      </div>
    );
  }
  return (
    <div className="relative" data-testid={testId}
      style={{ background: nextUp ? 'rgba(245,158,11,0.045)' : 'transparent', ...style }}>
      {nextUpTab}
      {/* sm and up: broadcast symmetric columns. Suppressed when forceStacked —
          e.g. narrow court panels on the live page, where two columns can't fit
          doubles names even on desktop (viewport breakpoints can't see that the
          CARD is narrow, so the caller opts in). */}
      {!forceStacked && (
      <div className="hidden sm:grid items-center"
        style={{ gridTemplateColumns: 'minmax(0,1fr) 84px minmax(0,1fr)',
          padding: nextUp ? '16px 14px 12px' : '12px 14px' }}>
        <TeamStack side="a" {...teamProps('a')} />
        <div className="flex flex-col items-center" style={{ gap: 5 }}>{center}</div>
        <TeamStack side="b" {...teamProps('b')} />
      </div>
      )}
      {/* phones (or any forceStacked context): stacked teams, center strip
          between — names stay whole */}
      <div className={forceStacked ? '' : 'sm:hidden'} style={{ padding: nextUp ? '15px 12px 10px' : '10px 12px' }}>
        <TeamStack side="a" stacked {...teamProps('a')} />
        <div className="flex items-center justify-center" style={{ gap: 8, padding: '5px 0' }}>{center}</div>
        <TeamStack side="b" stacked {...teamProps('b')} />
      </div>
    </div>
  );
}
